import React, { useEffect, useState } from 'react';
import { getClusterShare, putClusterShare } from '../api';

/**
 * Блок адміна на /cluster: «зробити сторінку видимою іншим» + пароль.
 *
 * Перегляд відкривається, лише коли галочка стоїть І пароль заданий. Будь-яка
 * зміна пароля, його видалення чи зняття галочки одразу закривають усі сесії
 * глядачів (сервер міняє версію пароля).
 */
export default function ClusterShareSettings() {
  const [cfg, setCfg] = useState(null);         // збережене на сервері
  const [enabled, setEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const apply = (d) => {
    setCfg(d);
    setEnabled(!!d.enabled);
    setPassword(d.password || '');
  };

  useEffect(() => {
    getClusterShare().then(r => apply(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message));
  }, []);

  const save = async (nextEnabled, nextPassword) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await putClusterShare(nextEnabled, nextPassword);
      apply(r.data);
      setMsg(r.data.open ? 'Збережено — сторінка відкрита за паролем'
                         : 'Збережено — сторінка закрита для інших');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onToggle = (e) => {
    const v = e.target.checked;
    setEnabled(v);
    // Зняття галочки — одразу на сервер: «закрити» не має чекати кнопки.
    // Увімкнення — лише разом із паролем (кнопка «Зберегти»).
    if (!v && cfg?.enabled) save(false, password);
  };

  if (!cfg && !err) return null;
  const dirty = cfg && (enabled !== !!cfg.enabled || password !== (cfg.password || ''));
  const link = `${window.location.origin}/cluster`;

  return (
    <div style={S.box}>
      <label style={S.row}>
        <input type="checkbox" checked={enabled} onChange={onToggle} disabled={busy} />
        <b>Зробити сторінку видимою іншим</b>
        {cfg?.open
          ? <span style={{ ...S.pill, ...S.on }}>відкрита за паролем</span>
          : <span style={{ ...S.pill, ...S.off }}>лише адмін</span>}
      </label>
      <div style={S.hint}>
        Людина з паролем бачить стан машин і може додавати нові (токени), але не
        може зупиняти, відкликати чи видаляти. Посилання:
        {/* Окремим рядком і без переносу: на телефоні моноширинне посилання
            не влазило на один знак і «r» з «/cluster» падала на новий рядок
            (менеджер 26.10). Шрифт сідає разом із шириною екрана. */}
        <a href={link} style={S.link}>{link}</a>
      </div>

      {enabled && (
        <div style={S.row}>
          <input
            type={show ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && password.trim()) save(true, password); }}
            placeholder="Задайте пароль для перегляду"
            autoComplete="new-password"
            style={S.input}
          />
          <button type="button" onClick={() => setShow(v => !v)} style={{ ...S.btn, ...S.ghost }}>
            {show ? 'Сховати' : 'Показати'}
          </button>
          <button type="button" onClick={() => save(true, password)}
                  disabled={busy || !password.trim() || !dirty}
                  style={{ ...S.btn, ...S.primary }}>
            Зберегти
          </button>
          {cfg?.password && (
            <button type="button"
                    onClick={() => {
                      if (window.confirm('Видалити пароль? Сторінка закриється для всіх, '
                                         + 'хто її зараз переглядає.')) save(false, '');
                    }}
                    disabled={busy} style={{ ...S.btn, ...S.ghost }}>
              Видалити пароль
            </button>
          )}
        </div>
      )}
      {enabled && cfg?.password && password !== cfg.password && (
        <div style={S.hint}>Після збереження нового пароля всім глядачам доведеться ввести його знову.</div>
      )}
      {msg && !dirty && <div style={S.ok}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}
    </div>
  );
}

const S = {
  box:   { border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px clamp(10px, 2.5vw, 14px)',
           margin: '0 0 16px', background: '#FAFAFB', maxWidth: 900 },
  row:   { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0' },
  hint:  { color: '#5B646F', fontSize: 13, lineHeight: 1.5, margin: '4px 0', wordBreak: 'break-word' },
  link:  { display: 'block', whiteSpace: 'nowrap', overflowX: 'auto', margin: '2px 0',
           fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
           fontSize: 'clamp(10px, 2.9vw, 13px)', color: '#1D4ED8' },
  input: { padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 6,
           fontSize: 14, minWidth: 0, flex: '1 1 220px', maxWidth: 320 },
  btn:   { padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
           fontSize: 13, cursor: 'pointer' },
  ghost: { background: '#FFFFFF', color: '#5B646F' },
  primary: { background: '#2563EB', color: '#FFFFFF', borderColor: '#2563EB' },
  pill:  { fontSize: 12, padding: '2px 8px', borderRadius: 10, fontWeight: 500 },
  on:    { background: '#DCFCE7', color: '#166534' },
  off:   { background: '#F3F4F6', color: '#5B646F' },
  ok:    { color: '#166534', fontSize: 13, marginTop: 4 },
  err:   { color: '#B91C1C', fontSize: 13, marginTop: 4 },
};
